import { OperatingSystem, detect, detectOS } from 'detect-browser'


export type ReactNativeDeviceInfo = {
    type: 'react-native'
    /**
     * Type values defined according to {@link OperatingSystem} from `detect-browser` package for convenience.
     */
    os?: 'iOS' | 'Android OS'
    osVersion: string
    platform: PLATFORM
    isTablet?: boolean
}
export type ArrisDeviceInfo = {
    type: 'arris'
    model?: 'VIP1003' | 'VIP1113' | 'VIP5305'
}
export type DeviceInfo = ArrisDeviceInfo | ReactNativeDeviceInfo
export type DeviceOs = OperatingSystem | 'Tizen' | 'webOS' | 'Arris' | 'Android TV' | 'tvOS'
export enum PLATFORM {
    /**
     * Includes tablets.
     */
    MOBILE = 'MOBILE',
    WEB = 'WEB',
    TV = 'TV',
    /**
     * @deprecated Used only in big query. In the future we'll migrate to undefined value and delete this.
     */
    UNKNOWN = 'unknown',
}

const TYPEOF_UNDEFINED = 'undefined'

/**
 * @public
 */
export type DeviceOsType = 'desktop' | 'mobile' | 'tv'

/**
 * @internal
 */
export const TV_OPERATING_SYSTEMS = ['Tizen', 'webOS', 'Android TV', 'tvOS']

/**
 * @internal
 */
export const MOBILE_OPERATING_SYSTEMS = ['iOS', 'Android OS', 'BlackBerry OS', 'Windows Mobile', 'Amazon OS']

/**
 * Web or smart TV
 *
 * Our smart TV apps run in a browser environment and support most of browser APIs,
 * we consider them browser-compatible.
 *
 * @returns true if browser / browser-compatible environment is detected
 * @public
 */
const isBrowserCompatible = () => {
    return typeof window !== TYPEOF_UNDEFINED
        && typeof document !== TYPEOF_UNDEFINED
}

const isNavigatorAvailable = () => {
    return typeof navigator !== TYPEOF_UNDEFINED
}

/**
 * Mobile web
 *
 * @returns true if mobile browser environment is detected
 * @public
 */
const isMobileBrowser = () => {
    if (!isNavigatorAvailable()) {
        return false
    }

    let check = false;
    // @ts-ignore
    (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) { check = true } })(navigator.userAgent || navigator.vendor || window.opera)
    return check
}

/**
 * Tablet web
 *
 * @returns true if tablet browser environment is detected
 * @public
 */
const isTabletBrowser = () => {
    if (!isNavigatorAvailable()) {
        return false
    }

    const userAgent = navigator?.userAgent?.toLowerCase()
    // source: https://tutorial.eyehunts.com/js/javascript-detect-mobile-or-tablet-html-example-code/
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)
}


/**
 * Detect React Native.
 *
 * @return true if ReactNative environment is detected.
 * @public
 */
const isReactNative = () => {
    return (
        typeof navigator === 'object' && navigator['product'] === 'ReactNative'
    )
}

/**
 * Detect native app - mobile only (not including tablets).
 *
 * @return true if native app running on mobile is detected.
 * @public
 */
const isNativeMobile = () => {
    const deviceInfo = getDeviceInfo()

    return isReactNative() && deviceInfo?.platform === PLATFORM.MOBILE && !deviceInfo?.isTablet
}

/**
 * Detect native app - tablet only.
 *
 * @return true if native app running on tablet is detected.
 * @public
 */
const isNativeTablet = () => {
    return isReactNative() && getDeviceInfo()?.isTablet
}


/**
 * @returns true if cookies are supported and work
 * @public
 */
const isCookiesEnabled = () => {
    // Apparently navigator.cookieEnabled is unreliable
    // https://stackoverflow.com/questions/2167310/how-to-show-a-message-only-if-cookies-are-disabled-in-browser/2167462#2167462

    // Just try to set and read a cookie

    if (!isBrowserCompatible() || typeof document.cookie !== 'string') {
        return false
    }

    document.cookie = 'tiviocookietest=1'
    const enabled = document.cookie.indexOf('tiviocookietest=') != -1

    // delete cookie by setting date in the past
    document.cookie = 'tiviocookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT'

    return enabled
}

/**
 * @return true if local storage is supported and works
 * @public
 */
const isLocalStorageEnabled = () => {
    if (!isBrowserCompatible() || typeof window.localStorage === TYPEOF_UNDEFINED) {
        return false
    }

    const testItem = 'tiviolocalstoragetest'

    try {
        localStorage.setItem(testItem, testItem)
        localStorage.removeItem(testItem)
        return true
    } catch (e) {
        return false
    }
}

/**
 * Tizen
 *
 * @returns true if is Tizen aka SAMSUNG TV
 * @public
 */
const isTizen = () => {
    // https://developer.samsung.com/smarttv/develop/api-references/tizen-web-device-api-references/tizen-api.html
    // @ts-ignore
    return isBrowserCompatible() && typeof window.tizen !== TYPEOF_UNDEFINED
}

/**
 * Webos
 *
 * @returns true if is Webos aka LG TV
 * @public
 */
const isWebos = () => {
    // https://webostv.developer.lge.com/api/webostvjs/intro-webostvjs/
    // @ts-ignore
    return isBrowserCompatible() && typeof window.webOS !== TYPEOF_UNDEFINED
}

/**
 * Arris STB
 *
 * @returns true if is Arris STB
 * @public
 */
const isArris = () => {
    if (device.info?.type === 'arris') {
        return true
    }

    // https://files.nangu.tv/tivio/files/arris-doc/doc/reference_toi3/toi3/index.html
    // @ts-ignore
    return isBrowserCompatible() && typeof window.toi !== TYPEOF_UNDEFINED
}

/**
 * Web-based smart TV
 *
 * @returns true if web-based smart TV is detected (which means it includes Arris STB but does NOT include native TVs)
 * @public
 */
const isWebTv = () => {
    return isTizen() || isWebos() || isArris()
}

/**
 * TV
 *
 * @returns true if TV device is detected.
 * @public
 */
const isTv = () => {
    return getDeviceInfo()?.platform === PLATFORM.TV
}

/**
 * Website
 *
 * @returns true if website environment is detected.
 * @public
 */
const isWebsite = () => {
    if (!isBrowserCompatible()) {
        return false
    }

    // For debugging purposes
    // @ts-ignore
    if (window.tivioDeviceOverride) {
        // @ts-ignore
        return window.tivioDeviceOverride === 'web'
    }

    return !isWebTv() && !isReactNative()
}

/**
 * Indexed DB
 *
 * @returns true if indexedDB API is available
 * @public
 */
const isIndexedDbSupported = () => {
    return window
        && window.indexedDB != null
        && window.IDBCursor != null
        && window.IDBCursorWithValue != null
        && window.IDBDatabase != null
        && window.IDBFactory != null
        && window.IDBIndex != null
        && window.IDBKeyRange != null
        && window.IDBObjectStore != null
        && window.IDBOpenDBRequest != null
        && window.IDBRequest != null
        && window.IDBTransaction != null
        && window.IDBVersionChangeEvent != null
}

/**
 * Safari web browser
 *
 * @returns true if Safari web browser is detected
 * @public
 */
const isSafari = () => {
    const info = detect()
    return info?.name == 'safari'
}

/**
 * iOS
 *
 * @returns true if iOS environment is detected
 * @public
 */
const isIos = () => {
    const info = detect()
    return info?.os == 'iOS'
}

/**
 * @public
 */
export type Device = {
    info: DeviceInfo | null,
}

/**
 * @public
 */
const device: Device = {
    info: null,
}

/**
 * @public
 */
const overrideDevice = (deviceInfo: DeviceInfo) => {
    device.info = deviceInfo
}

/**
 * @public
 */
const getArrisModelNumber = (model: string) => {
    const text = model.replace('VIP', '')
    const modelNumber = parseInt(text)

    if (Number.isFinite(modelNumber)) {
        return modelNumber
    } else {
        return null
    }
}

/**
 * Returns operating system of the device either based on DeviceInfo from tivio config or auto-detection.
 * If OS could not be determined, undefined will be returned.
 *
 * @public
 */
const getDeviceInfo = (): {
    os: DeviceOs
    version?: string
    platform: PLATFORM
    isTablet?: boolean
} | undefined => {
    // Forced override by tivio config (used when os cannot be reliably auto-detected).
    if (device.info) {
        // TODO this mapping is not pretty, can we merge typings with DeviceInfo from @tivio/types?
        // TODO problem is DeviceInfo.type is in lower case, and OperatingSystem is mixed case.
        switch (device.info.type) {
            case 'arris':
                return { os: 'Arris', platform: PLATFORM.TV }
            case 'react-native': {
                if (!device.info.os) {
                    break
                }

                let os: DeviceOs = device.info.os

                // split OS for mobile and TV
                if (os === 'Android OS') {
                    os = device.info.platform === PLATFORM.TV ? 'Android TV' : 'Android OS'
                } else if (os === 'iOS') {
                    os = device.info.platform === PLATFORM.TV ? 'tvOS' : 'iOS'
                }

                return {
                    os,
                    version: device.info.osVersion,
                    platform: device.info.platform,
                    isTablet: device.info.isTablet,
                }
            }
            default:
                // TODO can't import Logger class here, circular dependency
                console.warn(`Unexpected device info object ${JSON.stringify(device.info)}`)
                break
        }
    }

    // Smart TVs
    if (isTizen()) {
        return {
            os: 'Tizen',
            // TODO could potentially use getTizenVersion here, but it creates circular dependency
            platform: PLATFORM.TV,
        }
    }

    if (isWebos()) {
        return { os: 'webOS', platform: PLATFORM.TV }
    }

    if (isArris()) {
        return { os: 'Arris', platform: PLATFORM.TV }
    }

    // Web Browsers
    if (isNavigatorAvailable() && navigator.userAgent) {
        const os = detectOS(navigator.userAgent)

        return os ? { os, platform: PLATFORM.WEB } : undefined
    }

    // Cannot determine
    return undefined
}

/**
 * @returns true if is Arris version VIP4000 and higher
 * @public
 */
const isNewArris = (info: DeviceInfo | null) => {
    if (!info) {
        return false
    }

    if (info.type !== 'arris') {
        return false
    }

    const modelNumber = getArrisModelNumber(info.model ?? '')

    if (modelNumber == null) {
        return false
    }

    return modelNumber >= 4000
}

/**
 * Returns type of operating system.
 *
 * @param os operating system.
 * @public
 */
const getOsType = (os: DeviceOs): DeviceOsType => {
    if (TV_OPERATING_SYSTEMS.includes(os)) {
        return 'tv'
    } else if (MOBILE_OPERATING_SYSTEMS.includes(os)) {
        return 'mobile'
    } else {
        return 'desktop'
    }
}


export {
    getArrisModelNumber,
    getDeviceInfo,
    getOsType,
    isArris,
    isBrowserCompatible,
    isCookiesEnabled,
    isIndexedDbSupported,
    isIos,
    isLocalStorageEnabled,
    isMobileBrowser,
    isNativeMobile,
    isNativeTablet,
    isNewArris,
    isReactNative,
    isSafari,
    isWebTv,
    isTabletBrowser,
    isTizen,
    isTv,
    isWebos,
    isWebsite,
    overrideDevice,
}
